import React, { useState, useEffect } from 'react';
import { Card, Button, Placeholder, Row, Col, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import SoftBadge from 'components/common/SoftBadge';
import {
  getBillingAccount,
  subscribe,
  updateSubscription,
  getCustomerPortalUrl
} from 'helpers/requests/billing';
import { handleUnexpectedStatusCode } from 'helpers/errors';

const VM_TIERS = [1, 2, 3];
const PRICE_PER_VM = 19;

const TierCard = ({ tier, isSelected, isCurrent, label, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const isClickable = !isCurrent && onClick;

  return (
    <Card
      className={`h-100 text-center ${
        isSelected || isCurrent ? 'border-primary' : ''
      } ${hovered && isClickable ? 'shadow' : 'shadow-sm'}`}
      style={{
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s ease-in-out, transform 0.15s ease-in-out',
        transform: hovered && isClickable ? 'translateY(-2px)' : 'none'
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={isClickable ? onClick : undefined}
    >
      <Card.Body>
        <h5 className="mb-1">
          {tier} Cloud Desktop{tier !== 1 ? 's' : ''}
        </h5>
        <div className="text-700 mb-1">
          ${(tier * PRICE_PER_VM).toFixed(2)}/mo
        </div>
        {isCurrent && (
          <SoftBadge bg="primary" className="fs--2">
            Current
          </SoftBadge>
        )}
        {label && !isCurrent && (
          <span className="text-muted fs--1">{label}</span>
        )}
      </Card.Body>
    </Card>
  );
};

const Billing = () => {
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState({});
  const [selectedTier, setSelectedTier] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const fetchAccount = async () => {
    const r = await getBillingAccount();
    if (r.success) {
      setAccount(r.response.data);
      setSelectedTier(r.response.data.subscription_vm_limit || null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAccount();
  }, []);

  const handleSubscribe = async quantity => {
    setActionLoading(true);
    const r = await subscribe(quantity);
    if (r.success) {
      window.location.href = r.response.data.checkout_url;
    } else {
      if (r.response?.status === 400) {
        toast.error(r.response.data.message);
      } else {
        handleUnexpectedStatusCode(r.expectedStatusCode, r.response?.status);
      }
      setActionLoading(false);
    }
  };

  const handleUpdateSubscription = async quantity => {
    setConfirmModal(null);
    setActionLoading(true);
    const r = await updateSubscription(quantity);
    if (r.success) {
      toast.success('Subscription updated.');
      setSelectedTier(quantity);
      fetchAccount();
    } else {
      if (r.response?.status === 400) {
        toast.error(r.response.data.message);
      } else {
        handleUnexpectedStatusCode(r.expectedStatusCode, r.response?.status);
      }
    }
    setActionLoading(false);
  };

  const promptPlanChange = tier => {
    const currentLimit = account.subscription_vm_limit;
    const isUpgrade = tier > currentLimit;
    const oldTotal = (currentLimit * PRICE_PER_VM).toFixed(2);
    const newTotal = (tier * PRICE_PER_VM).toFixed(2);

    setConfirmModal({
      tier,
      isUpgrade,
      title: isUpgrade ? 'Upgrade plan' : 'Downgrade plan',
      body: isUpgrade
        ? `You're upgrading from ${currentLimit} to ${tier} Cloud Desktop${
            tier !== 1 ? 's' : ''
          }. Your new monthly rate will be $${newTotal}/mo (was $${oldTotal}/mo). You'll be charged a prorated amount for the remainder of this billing cycle.`
        : `You're downgrading from ${currentLimit} to ${tier} Cloud Desktop${
            tier !== 1 ? 's' : ''
          }. Your new monthly rate will be $${newTotal}/mo (was $${oldTotal}/mo). You'll receive a prorated credit for the remainder of this billing cycle.`
    });
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    const r = await getCustomerPortalUrl();
    if (r.success) {
      window.location.href = r.response.data.portal_url;
    } else {
      handleUnexpectedStatusCode(r.expectedStatusCode, r.response?.status);
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="mb-3">
        <Card.Header>
          <Placeholder as={Card.Text} animation="glow">
            <Placeholder xs={3} />
          </Placeholder>
        </Card.Header>
        <Card.Body className="pt-0">
          <Placeholder as={Card.Text} animation="glow">
            <Placeholder xs={5} />
          </Placeholder>
        </Card.Body>
      </Card>
    );
  }

  const {
    has_active_subscription,
    is_in_grace_period,
    subscription_status,
    subscription_vm_limit,
    subscription_current_period_end,
    subscription_cancel_at_period_end,
    demo_used,
    grace_period_ends_at
  } = account;

  return (
    <>
      {/* Status card */}
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Subscription</h6>
            <div>
              {has_active_subscription &&
                !subscription_cancel_at_period_end && (
                  <SoftBadge pill bg="success" className="fs--2">
                    Active &mdash; {subscription_vm_limit} Cloud Desktop
                    {subscription_vm_limit !== 1 ? 's' : ''}
                  </SoftBadge>
                )}
              {has_active_subscription && subscription_cancel_at_period_end && (
                <SoftBadge pill bg="warning" className="fs--2">
                  Canceling
                </SoftBadge>
              )}
              {subscription_status === 'none' && !is_in_grace_period && (
                <SoftBadge pill bg="secondary" className="fs--2">
                  No subscription
                </SoftBadge>
              )}
              {is_in_grace_period && (
                <SoftBadge pill bg="danger" className="fs--2">
                  Expired &mdash; Cloud Desktops suspended
                </SoftBadge>
              )}
              {subscription_status === 'canceled' && !is_in_grace_period && (
                <SoftBadge pill bg="secondary" className="fs--2">
                  Canceled
                </SoftBadge>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body className="pt-0">
          {/* Grace period warning */}
          {is_in_grace_period && grace_period_ends_at && (
            <div className="text-danger mb-3">
              Your Cloud Desktops are suspended and will be deleted on{' '}
              {new Date(grace_period_ends_at).toLocaleString()}. Subscribe now
              to restore access.
            </div>
          )}

          {/* No subscription, demo not used */}
          {subscription_status === 'none' &&
            !demo_used &&
            !is_in_grace_period && (
              <p className="text-muted mb-0">
                Try SwarmNode free &mdash; create a demo Cloud Desktop from the
                dashboard. When you're ready for more, subscribe below.
              </p>
            )}

          {/* No subscription, demo used, not in grace */}
          {subscription_status === 'none' &&
            demo_used &&
            !is_in_grace_period && (
              <p className="text-muted mb-0">
                Your free demo has ended. Subscribe to create and manage Cloud
                Desktops.
              </p>
            )}

          {/* Active subscription, canceling at period end */}
          {has_active_subscription &&
            subscription_cancel_at_period_end &&
            subscription_current_period_end && (
              <div className="text-warning mb-3">
                Your subscription is set to cancel on{' '}
                {new Date(subscription_current_period_end).toLocaleDateString()}
                . You'll keep access until then. To undo, visit Manage
                subscription below.
              </div>
            )}

          {/* Active subscription */}
          {has_active_subscription && !subscription_cancel_at_period_end && (
            <p className="text-muted mb-0">
              Your plan allows up to {subscription_vm_limit} Cloud Desktop
              {subscription_vm_limit !== 1 ? 's' : ''}. You can upgrade,
              downgrade, or manage payment below.
            </p>
          )}
        </Card.Body>
      </Card>

      {/* Desktop specs */}
      <Card className="mb-3">
        <Card.Header>
          <h6 className="mb-0">Cloud Desktop Specs</h6>
        </Card.Header>
        <Card.Body className="pt-0">
          <p className="text-muted mb-0">
            Each Cloud Desktop includes <strong>2 vCPUs</strong> and{' '}
            <strong>8 GB RAM</strong>.
          </p>
        </Card.Body>
      </Card>

      {/* Tier selection / subscribe */}
      {!has_active_subscription && (
        <Card className="mb-3">
          <Card.Header>
            <h6 className="mb-0">Choose a plan</h6>
          </Card.Header>
          <Card.Body className="pt-0">
            <Row className="g-3">
              {VM_TIERS.map(tier => (
                <Col md={4} key={tier}>
                  <TierCard
                    tier={tier}
                    isSelected={selectedTier === tier}
                    onClick={() => setSelectedTier(tier)}
                  />
                </Col>
              ))}
            </Row>
            <Button
              variant="primary"
              className="mt-3 w-100"
              disabled={!selectedTier || actionLoading}
              onClick={() => handleSubscribe(selectedTier)}
            >
              {actionLoading ? 'Redirecting...' : 'Subscribe'}
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Upgrade / downgrade for active subscribers */}
      {has_active_subscription && (
        <Card className="mb-3">
          <Card.Header>
            <h6 className="mb-0">Change plan</h6>
          </Card.Header>
          <Card.Body className="pt-0">
            <Row className="g-3">
              {VM_TIERS.map(tier => {
                const isCurrent = subscription_vm_limit === tier;
                let label = null;
                if (tier < subscription_vm_limit) label = 'Downgrade';
                if (tier > subscription_vm_limit) label = 'Upgrade';

                return (
                  <Col md={4} key={tier}>
                    <TierCard
                      tier={tier}
                      isCurrent={isCurrent}
                      label={label}
                      onClick={isCurrent ? null : () => promptPlanChange(tier)}
                    />
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Manage subscription */}
      {has_active_subscription && (
        <Card className="mb-3">
          <Card.Body>
            <Button
              variant="falcon-default"
              size="sm"
              disabled={actionLoading}
              onClick={handleManageSubscription}
            >
              Manage subscription (payment, cancel)
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Confirmation modal for plan changes */}
      <Modal
        show={!!confirmModal}
        onHide={() => setConfirmModal(null)}
        centered
      >
        {confirmModal && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{confirmModal.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{confirmModal.body}</Modal.Body>
            <Modal.Footer>
              <Button
                variant="falcon-default"
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={actionLoading}
                onClick={() => handleUpdateSubscription(confirmModal.tier)}
              >
                {actionLoading ? 'Updating...' : 'Confirm'}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default Billing;
